import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ContactPersonSourceType,
  displayPhoneNumber,
  IContactPerson,
  IInvalidField,
  accountingSystemProvider,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from 'shared';
import { When } from "react-if";
import { Tooltip } from "@mui/material";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from '@heroicons/react/24/outline';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import { Card } from "../../../../../common/Atoms/Card";
import { Heading } from "../../../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useContact } from "../../../../hooks/useContact";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Button } from '../../../../../common/Atoms/Button';
import { InputTextAddon } from '../../../../../common/Atoms/InputTextAddon';
import { Toggle } from '../../../../../common/Atoms/Toggle';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { ContactPersonInput, useUpdateContactPersonsMutation } from '../../../../services/api/contactsApi/contact';
import { MobileNumberFixModalContent } from "../../../Modals/MobileNumberFixModal";
import { InvalidMobileFixButton } from "../../CheckMobileNumbersButton";

import { SourceTypeSlider } from './SourceTypeSlider';
import { ContactPersonTagSelector } from './ContactPersonTagSelector';

function renderName(firstName?: string, lastName?: string) {
  if (!firstName && !lastName) {
    return `(No name found)`;
  }

  if (!firstName) {
    return lastName;
  }

  if (!lastName) {
    return firstName;
  }

  return `${firstName} ${lastName}`;
}

function renderIncludedElement(included: boolean, showExternalIncludeOverride: boolean) {
  if (included) {
    return (
      <div className={ `mt-1 flex items-center gap-x-1.5` }>
        <div className={ `flex-none rounded-full bg-emerald-500/20 p-1 animate-pulse` }>
          <div className={ `h-1.5 w-1.5 rounded-full bg-emerald-500` } />
        </div>
        <Paragraph
          variant={ `secondary` }
          size={ `xs` }
        >
          { `Included In Emails` }
        </Paragraph>
      </div>
    );
  }

  return (
    <div className={ `mt-1 flex items-center gap-x-1.5` }>
      { showExternalIncludeOverride ? renderExternalIncludeFlag(true) : (
        <div className={ `flex-none rounded-full bg-red-500/20 p-1 animate-pulse` }>
          <div className={ `h-1.5 w-1.5 rounded-full bg-red-500` } />
        </div>
      ) }
      <Paragraph
        variant={ `secondary` }
        size={ `xs` }
      >
        { `Excluded From Emails` }
      </Paragraph>
    </div>
  );
}

function renderExternalIncludeFlag(show: boolean) {
  if (!show) {
    return null;
  }

  return (
    <Tooltip
      title={ `Included externally, but currently excluded because the organisation setting "Only send to primary contact" is enabled.` }
      placement={ `top` }
    >
      <div className={ `inline-flex cursor-help items-center rounded-full bg-amber-100 p-1 text-amber-700` }>
        <ExclamationCircleIcon className={ `h-3.5 w-3.5` } />
      </div>
    </Tooltip>
  );
}

interface EditableContactPerson {
  localId: string;
  id?: string;
  isPrimary: boolean;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  mobileCountryCode: string;
  defaultNumber: string;
  defaultCountryCode: string;
  includeInNotifications: boolean;
  externalIncludeInNotifications: boolean;
  sourceType: ContactPersonSourceType;
  isNew: boolean;
}

interface SourceBadgeProps {
  sourceType: ContactPersonSourceType;
  importedProviderLabel: string;
  importedBadgeColor: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function renderSourceTypeLabel(sourceType: ContactPersonSourceType, importedProviderLabel: string) {
  if (sourceType === ContactPersonSourceType.LOCAL) {
    return `Paidnice Only`;
  }

  return importedProviderLabel;
}

function renderSourceTypeTooltip(sourceType: ContactPersonSourceType, importedProviderLabel: string) {
  if (sourceType === ContactPersonSourceType.LOCAL) {
    return `Stored in Paidnice only. This contact person is not synced back to ${importedProviderLabel}.`;
  }

  return `Imported from ${importedProviderLabel}. Changes are synced with ${importedProviderLabel}.`;
}

export function SourceBadge({
  sourceType,
  importedProviderLabel,
  importedBadgeColor,
}: SourceBadgeProps) {
  const isLocal = sourceType === ContactPersonSourceType.LOCAL;

  return (
    <Tooltip
      title={ renderSourceTypeTooltip(sourceType, importedProviderLabel) }
      placement={ `top` }
    >
      <span
        className={ `inline-flex cursor-help items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
          isLocal
            ? `border-paidniceGreen-300 bg-paidniceGreen-50 text-paidniceGreen-800`
            : `text-white`
        }` }
        style={ isLocal ? undefined : { borderColor: importedBadgeColor, backgroundColor: importedBadgeColor } }
      >
        { renderSourceTypeLabel(sourceType, importedProviderLabel) }
      </span>
    </Tooltip>
  );
}

function mapPersonToEditable(person: IContactPerson, isPrimary: boolean, fallbackLocalId: string): EditableContactPerson {
  const externalIncludeInNotifications = typeof person.externalIncludeInNotifications === `boolean`
    ? person.externalIncludeInNotifications
    : person.includeInNotifications;

  return {
    localId: person.id || fallbackLocalId,
    id: person.id,
    isPrimary,
    firstName: person.firstName || ``,
    lastName: person.lastName || ``,
    email: person.email || ``,
    mobileNumber: person.mobileNumber || ``,
    mobileCountryCode: person.mobileCountryCode || ``,
    defaultNumber: person.defaultNumber || ``,
    defaultCountryCode: person.defaultCountryCode || ``,
    includeInNotifications: externalIncludeInNotifications,
    externalIncludeInNotifications,
    sourceType: person.sourceType,
    isNew: false,
  };
}

function cleanOptionalString(value: string): string | undefined {
  const cleaned = value.trim();

  return cleaned ? cleaned : undefined;
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== `object`) {
    return `Failed to save contact persons.`;
  }

  const maybeError = error as {
    data?: unknown;
    error?: string;
  };

  if (typeof maybeError.error === `string` && maybeError.error) {
    return maybeError.error;
  }

  if (typeof maybeError.data === `string` && maybeError.data) {
    return maybeError.data;
  }

  if (
    maybeError.data &&
    typeof maybeError.data === `object` &&
    `message` in maybeError.data &&
    typeof maybeError.data.message === `string`
  ) {
    return maybeError.data.message;
  }

  return `Failed to save contact persons.`;
}

interface ContactPersonsProps {
  /** Render as a section inside a parent card — no Card wrapper, tighter spacing */
  embedded?: boolean;
  /** Opens straight into edit mode and calls `onDone` after saving or cancelling, e.g. inside a modal */
  editOnly?: boolean;
  onDone?: () => void;
}

export function ContactPersons({ embedded, editOnly, onDone }: ContactPersonsProps) {
  const { data: contact } = useContact();
  const currentOrg = useGetSelectedOrganisation();
  const [ updateContactPersons, { isLoading: savingPersons } ] = useUpdateContactPersonsMutation();
  const [ isEditing, setIsEditing ] = useState(false);
  const [ fixMobileOpen, setFixMobileOpen ] = useState(false);
  const [ saveError, setSaveError ] = useState<string | null>(null);
  const [ additionalPersonCount, setAdditionalPersonCount ] = useState(0);
  const [ primaryPerson, setPrimaryPerson ] = useState<EditableContactPerson | null>(null);
  const [ additionalPersons, setAdditionalPersons ] = useState<EditableContactPerson[]>([]);

  const provider = accountingSystemProvider(currentOrg);
  const importedProviderLabel = provider?.displayName ?? ``;
  const importedBadgeColor = provider?.brandColor ?? ``;
  const canSyncPersons = !!accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_SYNCED_CONTACT_PERSONS);
  const canEdit = Boolean(contact && currentOrg?.id);
  const importedAdditionalCount = additionalPersons.filter(person => person.sourceType === ContactPersonSourceType.IMPORTED).length;
  const canAddAdditionalPerson = !!accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ADDITIONAL_CONTACT_PERSONS);

  const people = useMemo(() => {
    if (!contact) {
      return [];
    }

    const persons = contact.contactPersons.map(person => {
      return {
        id: person.id,
        title: renderName(person.firstName, person.lastName),
        email: person.email,
        role: `Additional Contact`,
        sourceType: person.sourceType,
        includedInNotifications: person.includeInNotifications,
        externalIncludeInNotifications: person.externalIncludeInNotifications,
        personTags: person.personTags || [],
        mobileNumber: displayPhoneNumber(person.mobileCountryCode, person.mobileNumber),
        mobileNumberInvalid: person.mobileNumberInvalid,
        defaultNumber: displayPhoneNumber(person.defaultCountryCode, person.defaultNumber),
        emailError: person.emailError,
      };
    });

    persons.unshift({
      id: contact.primaryContactPerson.id,
      title: renderName(contact.primaryContactPerson.firstName, contact.primaryContactPerson.lastName),
      email: contact.primaryContactPerson.email,
      role: `Primary Contact`,
      sourceType: ContactPersonSourceType.IMPORTED,
      includedInNotifications: contact.primaryContactPerson.includeInNotifications,
      externalIncludeInNotifications: contact.primaryContactPerson.externalIncludeInNotifications,
      personTags: contact.primaryContactPerson.personTags || [],
      mobileNumber: displayPhoneNumber(contact.primaryContactPerson.mobileCountryCode, contact.primaryContactPerson.mobileNumber),
      mobileNumberInvalid: contact.primaryContactPerson.mobileNumberInvalid,
      defaultNumber: displayPhoneNumber(contact.primaryContactPerson.defaultCountryCode, contact.primaryContactPerson.defaultNumber),
      emailError: contact.primaryContactPerson.emailError,
    });

    return persons;
  }, [ contact ]);

  const primaryEmailInvalid = Boolean(primaryPerson?.email.trim()) && !emailRegex.test(primaryPerson?.email.trim() || ``);
  const additionalEmailInvalid = additionalPersons.some(person => {
    return Boolean(person.email.trim()) && !emailRegex.test(person.email.trim());
  });
  const canSave = Boolean(primaryPerson) && !primaryEmailInvalid && !additionalEmailInvalid;

  function enterEditMode() {
    if (!contact) {
      return;
    }

    setSaveError(null);
    setPrimaryPerson(mapPersonToEditable(contact.primaryContactPerson, true, `primary`));

    setAdditionalPersons(
      contact.contactPersons.map((person, index) => mapPersonToEditable(person, false, `additional-${index}`)),
    );

    setAdditionalPersonCount(contact.contactPersons.length);
    setIsEditing(true);
  }

  useEffect(() => {
    if (editOnly && contact) {
      enterEditMode();
    }
  }, [editOnly, !!contact]);

  function cancelEditMode() {
    setIsEditing(false);
    setSaveError(null);
    onDone?.();
  }

  function updatePrimaryField(field: keyof EditableContactPerson, value: string | boolean) {
    if (!primaryPerson) {
      return;
    }

    setPrimaryPerson({
      ...primaryPerson,
      [field]: value,
    });
  }

  function updateAdditionalField(localId: string, field: keyof EditableContactPerson, value: string | boolean) {
    setAdditionalPersons(prev => {
      return prev.map(person => {
        if (person.localId !== localId) {
          return person;
        }

        return {
          ...person,
          [field]: value,
        };
      });
    });
  }

  function addAdditionalPerson() {
    if (!canAddAdditionalPerson) {
      return;
    }

    const localIndex = additionalPersonCount + 1;
    setAdditionalPersonCount(localIndex);

    setAdditionalPersons(prev => [
      ...prev,
      {
        localId: `new-${localIndex}`,
        isPrimary: false,
        firstName: ``,
        lastName: ``,
        email: ``,
        mobileNumber: ``,
        mobileCountryCode: ``,
        defaultNumber: ``,
        defaultCountryCode: ``,
        includeInNotifications: true,
        externalIncludeInNotifications: true,
        sourceType: !canSyncPersons || importedAdditionalCount >= 5 ? ContactPersonSourceType.LOCAL : ContactPersonSourceType.IMPORTED,
        isNew: true,
      },
    ]);
  }

  function removeAdditionalPerson(localId: string) {
    setAdditionalPersons(prev => prev.filter(person => person.localId !== localId));
  }

  async function savePersons() {
    if (!contact?.id || !currentOrg?.id || !primaryPerson || !canSave) {
      return;
    }

    setSaveError(null);

    const persons: ContactPersonInput[] = [
      {
        ...(primaryPerson.id ? { id: primaryPerson.id } : {}),
        isPrimary: true,
        firstName: cleanOptionalString(primaryPerson.firstName),
        lastName: cleanOptionalString(primaryPerson.lastName),
        email: cleanOptionalString(primaryPerson.email),
        mobileNumber: cleanOptionalString(primaryPerson.mobileNumber),
        mobileCountryCode: cleanOptionalString(primaryPerson.mobileCountryCode),
        defaultNumber: cleanOptionalString(primaryPerson.defaultNumber),
        defaultCountryCode: cleanOptionalString(primaryPerson.defaultCountryCode),
        includeInNotifications: primaryPerson.externalIncludeInNotifications,
      },
      ...additionalPersons.map(person => ({
        ...(person.id ? { id: person.id } : {}),
        isPrimary: false,
        firstName: cleanOptionalString(person.firstName),
        lastName: cleanOptionalString(person.lastName),
        email: cleanOptionalString(person.email),
        includeInNotifications: person.externalIncludeInNotifications,
        ...(person.isNew ? { sourceType: person.sourceType } : {}),
      })),
    ];

    try {
      await updateContactPersons({
        organisationId: currentOrg.id,
        contactId: contact.id,
        persons,
      }).unwrap();

      setIsEditing(false);
      onDone?.();
    }
    catch (error) {
      setSaveError(getErrorMessage(error));
    }
  }

  const Wrapper = embedded ? `div` : Card;

  return (
    <Wrapper>
      <div className={ `flex justify-between` }>
        <Heading size={ embedded ? `sm` : `md` }>
          { `Contact People` }
        </Heading>
        <When condition={ !isEditing }>
          <Button
            isIcon
            size={ `sm` }
            color={ `none` }
            className={ `h-8 w-8 flex items-center justify-center border-0 shadow-none bg-transparent hover:bg-transparent focus:ring-0 text-paidniceGreen-600` }
            tooltip={ `Edit contact` }
            disabled={ !canEdit }
            onClick={ enterEditMode }
          >
            <PencilSquareIcon className={ `h-5 w-5` } />
          </Button>
        </When>
      </div>

      <When condition={ isEditing }>
        <div className={ `mt-5 space-y-6` }>
          <When condition={ saveError }>
            <Notification
              type={ `error` }
            >
              { saveError }
            </Notification>
          </When>
          <div className={ `rounded-md border border-gray-200 p-4` }>
            <div className={ `flex items-center justify-between gap-2` }>
              <Paragraph>
                { `Primary Contact` }
              </Paragraph>
              <SourceBadge
                sourceType={ ContactPersonSourceType.IMPORTED }
                importedProviderLabel={ importedProviderLabel }
                importedBadgeColor={ importedBadgeColor }
              />
            </div>
            <div className={ `mt-3 grid grid-cols-1 gap-4 md:grid-cols-2` }>
              <InputTextAddon
                label={ `First Name` }
                value={ primaryPerson?.firstName || `` }
                onChange={ e => updatePrimaryField(`firstName`, e.value as string) }
              />
              <InputTextAddon
                label={ `Last Name` }
                value={ primaryPerson?.lastName || `` }
                onChange={ e => updatePrimaryField(`lastName`, e.value as string) }
              />
              <InputTextAddon
                label={ `Email` }
                value={ primaryPerson?.email || `` }
                onChange={ e => updatePrimaryField(`email`, e.value as string) }
                invalid={
                  primaryEmailInvalid ?
                    { message: `Please enter a valid email address.` } as IInvalidField
                    : undefined
                }
              />
              <Toggle
                label={ `Include in notifications` }
                checked={ !!primaryPerson?.externalIncludeInNotifications }
                name={ `includeInNotifications` }
                disabled
                onChange={ () => {} }
              />
              <InputTextAddon
                label={ `Mobile Country Code` }
                value={ primaryPerson?.mobileCountryCode || `` }
                onChange={ e => updatePrimaryField(`mobileCountryCode`, e.value as string) }
              />
              <InputTextAddon
                label={ `Mobile Number` }
                value={ primaryPerson?.mobileNumber || `` }
                onChange={ e => updatePrimaryField(`mobileNumber`, e.value as string) }
              />
              <InputTextAddon
                label={ `Phone Country Code` }
                value={ primaryPerson?.defaultCountryCode || `` }
                onChange={ e => updatePrimaryField(`defaultCountryCode`, e.value as string) }
              />
              <InputTextAddon
                label={ `Phone Number` }
                value={ primaryPerson?.defaultNumber || `` }
                onChange={ e => updatePrimaryField(`defaultNumber`, e.value as string) }
              />
            </div>
            <When condition={ !!primaryPerson?.externalIncludeInNotifications && !primaryPerson?.includeInNotifications }>
              <div className={ `mt-3` }>
                <Notification type={ `warning` }>
                  { `Included externally, but currently excluded because the organisation setting "Only send to primary contact" is enabled.` }
                </Notification>
              </div>
            </When>
          </div>

          <When condition={ canAddAdditionalPerson }>
            <div className={ `space-y-4` }>
              <div className={ `flex justify-between items-center` }>
                <Paragraph>
                  { `Additional Contacts` }
                </Paragraph>
              </div>
              <When condition={ !canSyncPersons }>
                <Notification type={ `info` }>
                  { `Additional contacts are stored in Paidnice only and are not sent back to ${importedProviderLabel}.` }
                </Notification>
              </When>
              { additionalPersons.map(person => {
                const emailInvalid = Boolean(person.email.trim()) && !emailRegex.test(person.email.trim());
                const canEnableSyncToXero = person.sourceType === ContactPersonSourceType.IMPORTED || importedAdditionalCount < 5;
                const syncToXeroDisabled = person.isNew && person.sourceType !== ContactPersonSourceType.IMPORTED && !canEnableSyncToXero;

                return (
                  <div
                    key={ person.localId }
                    className={ `rounded-md border border-gray-200 p-4` }
                  >
                    <div className={ `mb-4 flex items-center justify-between gap-2` }>
                      <Paragraph variant={ `secondary` }>
                        { `Additional Contact` }
                      </Paragraph>
                      <SourceBadge
                        sourceType={ person.sourceType }
                        importedProviderLabel={ importedProviderLabel }
                        importedBadgeColor={ importedBadgeColor }
                      />
                    </div>
                    <div className={ `grid grid-cols-1 gap-4 md:grid-cols-2` }>
                      <InputTextAddon
                        label={ `First Name` }
                        value={ person.firstName }
                        onChange={ e => updateAdditionalField(person.localId, `firstName`, e.value as string) }
                      />
                      <InputTextAddon
                        label={ `Last Name` }
                        value={ person.lastName }
                        onChange={ e => updateAdditionalField(person.localId, `lastName`, e.value as string) }
                      />
                      <InputTextAddon
                        label={ `Email` }
                        className={ `md:col-span-2` }
                        value={ person.email }
                        onChange={ e => updateAdditionalField(person.localId, `email`, e.value as string) }
                        invalid={
                          emailInvalid ?
                            { message: `Please enter a valid email address.` } as IInvalidField
                            : undefined
                        }
                      />
                      <div className={ `md:col-span-2 flex items-center justify-between gap-3` }>
                        <div className={ `flex-1` }>
                          <Toggle
                            label={ `Include in notifications` }
                            checked={ person.externalIncludeInNotifications }
                            name={ `includeInNotifications` }
                            onChange={ e => {
                              updateAdditionalField(person.localId, `includeInNotifications`, e.value);
                              updateAdditionalField(person.localId, `externalIncludeInNotifications`, e.value);
                            } }
                          />
                          <When condition={ person.isNew && canSyncPersons }>
                            <div className={ `mt-3` }>
                              <SourceTypeSlider
                                value={ person.sourceType }
                                providerName={ importedProviderLabel }
                                disableImportedOption={ syncToXeroDisabled }
                                onChange={ value => {
                                  if (value === ContactPersonSourceType.IMPORTED && importedAdditionalCount >= 5) {
                                    updateAdditionalField(person.localId, `sourceType`, ContactPersonSourceType.LOCAL);

                                    return;
                                  }

                                  updateAdditionalField(person.localId, `sourceType`, value);
                                } }
                              />
                              <When condition={ syncToXeroDisabled }>
                                <Paragraph
                                  variant={ `secondary` }
                                  size={ `xs` }
                                  className={ `mt-1` }
                                >
                                  { `Xero allows max 5 additional synced contacts; this will be saved locally` }
                                </Paragraph>
                              </When>
                            </div>
                          </When>
                        </div>
                        <Button
                          isIcon
                          size={ `sm` }
                          color={ `paidniceRedOutline` }
                          className={ `h-8 w-8 flex items-center justify-center` }
                          onClick={ () => removeAdditionalPerson(person.localId) }
                        >
                          <TrashIcon className={ `h-4 w-4` } />
                        </Button>
                      </div>
                    </div>
                    <When condition={ person.externalIncludeInNotifications && !person.includeInNotifications }>
                      <div className={ `mt-3` }>
                        <Notification type={ `warning` }>
                          { `Included externally, but currently excluded because the organisation setting "Only send to primary contact" is enabled.` }
                        </Notification>
                      </div>
                    </When>
                  </div>
                );
              }) }
              <When condition={ canAddAdditionalPerson }>
                <div className={ `flex justify-center` }>
                  <Button
                    size={ `s` }
                    color={ `paidniceGreenOutline` }
                    onClick={ addAdditionalPerson }
                  >
                    { `Add contact person` }
                  </Button>
                </div>
              </When>
            </div>
          </When>

          <div className={ `flex justify-end gap-3` }>
            <Button
              color={ `gray` }
              onClick={ cancelEditMode }
              disabled={ savingPersons }
            >
              { `Cancel` }
            </Button>
            <Button
              onClick={ savePersons }
              disabled={ !canSave || savingPersons }
              loading={ savingPersons }
              loadingText={ `Saving...` }
            >
              { `Save` }
            </Button>
          </div>
        </div>
      </When>

      <When condition={ !isEditing }>
        <ul
          role={ `list` }
          className={ `divide-y divide-gray-100` }
        >
          { people.map((person, index) => (
            <li
              key={ `${person.email}-${index}` }
              className={ `` }
            >
              <div className={ `flex justify-between gap-x-6 ${embedded ? `py-3` : `py-5`}` }>
                <div className={ `flex min-w-0 gap-x-4` }>
                  <div className={ `min-w-0 flex-auto` }>
                    <div className={ `flex items-center gap-2` }>
                      <Paragraph>
                        { person.title }
                      </Paragraph>
                      <SourceBadge
                        sourceType={ person.sourceType }
                        importedProviderLabel={ importedProviderLabel }
                        importedBadgeColor={ importedBadgeColor }
                      />
                    </div>
                    <Paragraph
                      variant={ `secondary` }
                      className={ `truncate` }
                      tooltipped
                    >
                      { person.email }
                    </Paragraph>
                    { /* When a contact has a default number but no mobile. Still show mobile to highlight it being empty */ }
                    <When condition={ person.mobileNumber || person.defaultNumber }>
                      <Paragraph
                        variant={ `secondary` }
                      >
                        <span>
                          { `Mobile: ` }
                        </span>
                        { person.mobileNumber }
                        <When condition={ person.mobileNumberInvalid }>
                          <InvalidMobileFixButton
                            size={ `sm` }
                            onClick={ () => setFixMobileOpen(true) }
                          />
                        </When>
                      </Paragraph>
                    </When>

                    <When condition={ person.defaultNumber }>
                      <Paragraph
                        variant={ `secondary` }
                      >
                        <span>
                          { `Phone: ` }
                        </span>
                        { person.defaultNumber }
                      </Paragraph>
                    </When>
                    <When condition={ !!person.id }>
                      <ContactPersonTagSelector
                        personId={ person.id || `` }
                        personTags={ person.personTags }
                      />
                    </When>
                  </div>
                </div>
                <div className={ `hidden shrink-0 sm:flex sm:flex-col sm:items-end` }>
                  <Paragraph>
                    { person.role }
                  </Paragraph>

                  { renderIncludedElement(
                    person.includedInNotifications,
                    Boolean(person.externalIncludeInNotifications) && !person.includedInNotifications,
                  ) }
                </div>
              </div>

              <When condition={ person.emailError }>
                <Notification
                  type={ `warning` }
                >
                  { person.emailError }
                </Notification>
              </When>
            </li>
          )) }
        </ul>
      </When>
      <MobileNumberFixModalContent
        open={ fixMobileOpen }
        hide={ () => setFixMobileOpen(false) }
        contactId={ contact?.id }
      />
    </Wrapper>
  );
}
